import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import OutsideClickHandler from 'react-outside-click-handler';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import 'react-datepicker/dist/react-datepicker.css';
import './SalesAndPromotionsDateRangePicker.scss';

const SalesAndPromotionsDateRangePicker = ({ fromDate, toDate, onApply }) => {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [selectingEnd, setSelectingEnd] = useState(false);
  const buttonRef = useRef(null);
  const close = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };
  const selectPreset = preset => {
    const today = moment();
    const from =
      preset === 'Last month'
        ? today
            .clone()
            .subtract(1, 'month')
            .startOf('month')
        : today.clone().subtract(preset === 'Last 7 days' ? 6 : 29, 'days');
    const to = preset === 'Last month' ? from.clone().endOf('month') : today;
    setStart(preset === 'All dates' ? '' : from.format('YYYY-MM-DD'));
    setEnd(preset === 'All dates' ? '' : to.format('YYYY-MM-DD'));
    setSelectingEnd(false);
  };
  const selectDay = date => {
    const day = moment(date).format('YYYY-MM-DD');
    if (selectingEnd && day >= start) {
      setEnd(day);
      setSelectingEnd(false);
    } else {
      setStart(day);
      setEnd('');
      setSelectingEnd(true);
    }
  };
  const canApply = (!start && !end) || (start && end && end >= start);
  return (
    <OutsideClickHandler onOutsideClick={() => setOpen(false)}>
      <div
        className='sales-and-promotions-date-range-picker'
        onKeyDown={event => {
          if (event.key === 'Escape' && open) {
            event.stopPropagation();
            close();
          }
        }}
      >
        <span className='date-range-label'>Sale dates</span>
        <button
          type='button'
          className='date-range-trigger'
          ref={buttonRef}
          aria-expanded={open}
          aria-controls='sales-date-range'
          onClick={() => {
            setStart(fromDate || '');
            setEnd(toDate || '');
            setSelectingEnd(false);
            setOpen(!open);
          }}
        >
          <FontAwesomeIcon icon={faCalendar} />
          {fromDate && toDate ? `${moment(fromDate).format('MMM D, YYYY')} – ${moment(toDate).format('MMM D, YYYY')}` : 'All dates'}
        </button>
        {open && (
          <section id='sales-date-range' className='date-range-popup' aria-label='Choose sale dates'>
            <div className='date-range-body'>
              <aside className='date-presets'>
                <strong>Date range</strong>
                {['Last 7 days', 'Last 30 days', 'Last month', 'All dates'].map(preset => (
                  <button type='button' key={preset} onClick={() => selectPreset(preset)}>
                    {preset}
                  </button>
                ))}
              </aside>
              <div className='date-calendars'>
                <div className='date-inputs'>
                  <label>
                    Start date
                    <input
                      type='date'
                      value={start}
                      onChange={event => {
                        setStart(event.target.value);
                        setSelectingEnd(true);
                      }}
                    />
                  </label>
                  <span aria-hidden='true'>–</span>
                  <label>
                    End date
                    <input
                      type='date'
                      value={end}
                      min={start || undefined}
                      onChange={event => {
                        setEnd(event.target.value);
                        setSelectingEnd(false);
                      }}
                    />
                  </label>
                </div>
                <p aria-live='polite'>{selectingEnd ? 'Choose an end date' : 'Choose a start date'} · Eastern time</p>
                <DatePicker
                  inline
                  monthsShown={2}
                  selected={start ? moment(start).toDate() : null}
                  startDate={start ? moment(start).toDate() : null}
                  endDate={end ? moment(end).toDate() : null}
                  selectsStart={!selectingEnd}
                  selectsEnd={selectingEnd}
                  onChange={selectDay}
                />
              </div>
            </div>
            <div className='date-range-actions'>
              <button type='button' className='cancel-dates' onClick={close}>
                Cancel
              </button>
              <button
                type='button'
                className='apply-dates'
                disabled={!canApply}
                onClick={() => {
                  onApply(start || undefined, end || undefined);
                  close();
                }}
              >
                Apply
              </button>
            </div>
          </section>
        )}
      </div>
    </OutsideClickHandler>
  );
};
SalesAndPromotionsDateRangePicker.propTypes = {
  fromDate: PropTypes.string,
  toDate: PropTypes.string,
  onApply: PropTypes.func.isRequired
};
export default SalesAndPromotionsDateRangePicker;
